import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import TierBadge, { TIER_STYLE } from '../components/TierBadge'
import { pickDetail } from '../lib/data'

const TIERS = ['critical','high','medium','low']

// Sort modes — default is by priority_score so the most-broken sites
// rise to the top. "Name" gives the old alphabetical view if someone
// is hunting for a specific site by name.
const SORT_MODES = {
  priority: { label: 'Priority', cmp: (a, b) => (b._score - a._score) },
  name:     { label: 'Name',     cmp: (a, b) => a.name.localeCompare(b.name) },
}

export default function Sites() {
  const navigate = useNavigate()
  const [sites, setSites] = useState([])
  const [openTicketCounts, setOpenTicketCounts] = useState({})  // site_id -> count
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [tierFilter, setTierFilter] = useState('all')
  const [activeFilter, setActiveFilter] = useState('active')    // 'active' | 'all'
  const [sortMode, setSortMode] = useState('priority')

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    // Pull sites + diagnostic fields in one round trip, plus an aggregate
    // of open tickets per site so we can show the count inline. Tickets
    // pull is bounded by status so it stays small even at scale.
    const activeStatuses = ['open','scheduled','dispatched','remote','in_progress','blocked']
    const [sitesRes, ticketsRes] = await Promise.all([
      supabase.from('sites')
        .select(`id, name, address, city, state, active, site_details(
          priority_tier, priority_score, priority_rank, site_code,
          district, region, camera_mount, gate_type, manual_lp_pct
        )`)
        .order('name'),
      supabase.from('tickets')
        .select('site_id')
        .in('status', activeStatuses),
    ])
    setSites(sitesRes.data || [])
    const counts = {}
    for (const t of (ticketsRes.data || [])) {
      counts[t.site_id] = (counts[t.site_id] || 0) + 1
    }
    setOpenTicketCounts(counts)
    setLoading(false)
  }

  const filteredAndSorted = useMemo(() => {
    const q = search.trim().toLowerCase()
    const items = sites
      .filter(s => {
        if (activeFilter === 'active' && !s.active) return false
        const det = pickDetail(s.site_details)
        if (tierFilter !== 'all' && det?.priority_tier !== tierFilter) return false
        if (!q) return true
        // Search across name, code, city, district — cover the most natural
        // ways someone references a site.
        const hay = [
          s.name, s.city, s.address,
          det?.site_code, det?.district, det?.region,
        ].filter(Boolean).join(' ').toLowerCase()
        return hay.includes(q)
      })
      .map(s => ({
        ...s,
        // Annotate the score we'll sort by — null scores sink to bottom
        // when sorting by priority so unsurveyed sites don't lead.
        _score: Number(pickDetail(s.site_details)?.priority_score ?? -1),
      }))
    items.sort(SORT_MODES[sortMode].cmp)
    return items
  }, [sites, search, tierFilter, activeFilter, sortMode])

  if (loading) return <div className="spinner" />

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <div className="page-title">Sites</div>
          <div className="page-subtitle">{sites.length} total · {sites.filter(s => s.active).length} active</div>
        </div>
      </div>

      <input
        className="form-input"
        style={{ marginBottom: 10 }}
        placeholder="Search by name, code, city, district…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        <button
          className={`btn btn-sm ${tierFilter === 'all' ? 'btn-primary' : ''}`}
          onClick={() => setTierFilter('all')}>All tiers</button>
        {TIERS.map(t => (
          <button key={t}
            className={`btn btn-sm ${tierFilter === t ? 'btn-primary' : ''}`}
            onClick={() => setTierFilter(t)}>
            {TIER_STYLE[t]?.dot} {TIER_STYLE[t]?.label}
          </button>
        ))}
        <span style={{ marginLeft: 'auto' }} />
        <button
          className={`btn btn-sm ${activeFilter === 'active' ? 'btn-primary' : ''}`}
          onClick={() => setActiveFilter('active')}>Active</button>
        <button
          className={`btn btn-sm ${activeFilter === 'all' ? 'btn-primary' : ''}`}
          onClick={() => setActiveFilter('all')}>All</button>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, fontSize: 12, color: 'var(--text-muted)' }}>
        <span>{filteredAndSorted.length} of {sites.length} sites</span>
        <span style={{ marginLeft: 'auto' }}>Sort:</span>
        {Object.entries(SORT_MODES).map(([k, m]) => (
          <button key={k}
            className={`btn btn-sm ${sortMode === k ? 'btn-primary' : ''}`}
            onClick={() => setSortMode(k)}>
            {m.label}
          </button>
        ))}
      </div>

      {filteredAndSorted.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📍</div>
          <h3>No sites match</h3>
          <p>Try clearing filters or searching for something else.</p>
        </div>
      ) : (
        filteredAndSorted.map(s => {
          const det = pickDetail(s.site_details)
          const openCount = openTicketCounts[s.id] || 0
          // Quick visual for the operational warnings we have on hand
          const isStandCam = det?.camera_mount === 'stand'
          const highManual = Number(det?.manual_lp_pct ?? 0) > 0.10
          return (
            <div key={s.id} className="card"
              style={{ marginBottom: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
              onClick={() => navigate(`/sites/${s.id}`)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {det?.site_code && (
                    <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                      {det.site_code}
                    </span>
                  )}
                  <span style={{ fontWeight: 500 }}>{s.name}</span>
                  <TierBadge tier={det?.priority_tier} rank={det?.priority_rank} />
                  {!s.active && (
                    <span className="badge badge-na">Inactive</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>{[s.city, s.state].filter(Boolean).join(', ') || '—'}</span>
                  {det?.district && <span>· {det.district}</span>}
                  {isStandCam && <span style={{ color: '#A32D2D' }}>· ⚠️ stand cams</span>}
                  {highManual && <span style={{ color: '#A32D2D' }}>· ⚠️ {(det.manual_lp_pct * 100).toFixed(0)}% manual</span>}
                </div>
              </div>
              {openCount > 0 && (
                <span style={{
                  fontSize: 11, padding: '3px 9px', borderRadius: 10,
                  background: '#FCEBEB', color: '#A32D2D', fontWeight: 500,
                  flexShrink: 0,
                }}>
                  {openCount} open
                </span>
              )}
            </div>
          )
        })
      )}
    </div>
  )
}
